import { api } from "network/network";
import { API_HOST_HP_INTERNAL } from "config/api.config";
import {
  setCookie,
  getCookie,
  deleteCookie,
  AUTH_TOKEN_COOKIE,
  OUTLET_ID_COOKIE,
} from "utils/cookies";

export const EXTERNAL_ENDPOINTS = {
  verifyUser: "/api/verifyUser",
  sendOtpSms: "/api/user/otpsms",
  signIn: "/api/registration/signin",
  signInUser: "/consumer/signInUser/v2",
  rkOutlets: "/ext/rk/outlets",
  rkOrders: "/ext/rk/orders",
  rkIssueOrders: "/ext/rk/orders/issues",
  rkReturnDetails: "/ext/rk/return",
  rkCategories: "/ext/rk/categories",
  rkSearch: "/ext/rk/search",
  rkReturns: "/ext/rk/returns",
  rkReturnImage: "/ext/rk/returns/image",
  rkUser: "/ext/rk/user",
};

export const ACTION_TYPES = {
  SIGN_IN_OTP: "SIGN_IN_OTP",
};

export const verifyUser = ({ phoneNumber }) =>
  api.get(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.verifyUser, {
    query: { phoneNumber },
  });

export const sendOtpSms = ({ phoneNumber, source = "sign_in" }) =>
  api.post(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.sendOtpSms, undefined, {
    query: {
      isForgotPassword: true,
      userPhoneNumber: phoneNumber,
      source,
    },
  });

export const signIn = ({ phoneNumber, otp }) =>
  api.post(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.signIn, {
    Name: phoneNumber,
    OTP: otp,
  });

export const signInUser = () =>
  api.get(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.signInUser);

export const getRkOutlets = ({ keyword } = {}) => {
  const query = {};
  if (keyword) query.outlet_keyword = keyword;
  return api.get(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.rkOutlets, {
    query,
  });
};

export const getRkOrders = ({ outletId, ordersCount = 10 }) =>
  api.get(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.rkOrders, {
    query: { outlet_id: outletId, orders_count: ordersCount },
  });

export const getRkIssueOrders = ({ outletName } = {}) => {
  const query = {};
  if (outletName) query.outlet_name = outletName;
  return api.get(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.rkIssueOrders, {
    query,
  });
};

export const getRkReturnDetails = ({ orderId }) =>
  api.get(
    API_HOST_HP_INTERNAL,
    `${EXTERNAL_ENDPOINTS.rkReturnDetails}/${encodeURIComponent(orderId)}`
  );

export const getRkCategories = ({ outletId } = {}) => {
  const query = { fetch_only_sub_categories: true };
  if (outletId != null) query.outlet_id = outletId;
  return api.get(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.rkCategories, {
    query,
  });
};

export const searchRkProducts = ({
  outletId,
  categoryIds,
  keyword,
  pageNumber = 1,
} = {}) => {
  const query = {};
  if (outletId != null) query.outlet_id = outletId;
  if (categoryIds != null) query.category_ids = categoryIds;
  if (keyword) query.query = keyword;
  query.page_no = pageNumber;
  return api.get(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.rkSearch, {
    query,
  });
};

export const uploadRkReturnImage = ({ file, orderId }) => {
  const formData = new FormData();
  formData.append("file", file, file.name);
  if (orderId != null && orderId !== "") {
    formData.append("order_id", String(orderId));
  }
  return api.post(
    API_HOST_HP_INTERNAL,
    EXTERNAL_ENDPOINTS.rkReturnImage,
    formData
  );
};

export const markRkReturn = (returnDetails) =>
  api.post(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.rkReturns, returnDetails);

export const getRkUser = () =>
  api.get(API_HOST_HP_INTERNAL, EXTERNAL_ENDPOINTS.rkUser);

const toErrorMessage = (value) => {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value?.message === "string" && value.message.trim()) {
    return value.message;
  }
  return "";
};

const extractErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  return (
    [data?.message, data?.error, data?.errorMessage, data, err?.message]
      .map(toErrorMessage)
      .find(Boolean) || fallback
  );
};

export async function verifyUserAndSendOtp({ phoneNumber }) {
  let verifyResp;
  try {
    verifyResp = await verifyUser({ phoneNumber });
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) {
      return {
        success: false,
        message: extractErrorMessage(err, "User not found"),
      };
    }
    return {
      success: false,
      message: extractErrorMessage(
        err,
        "Something went wrong. Please try again."
      ),
    };
  }

  const body = verifyResp?.data ?? {};
  const actionType = body?.response?.ActionData?.ActionType;

  if (!actionType) {
    return { success: false, message: "Action missing" };
  }

  if (actionType !== ACTION_TYPES.SIGN_IN_OTP) {
    return { success: false, message: "Action missing" };
  }

  try {
    await sendOtpSms({ phoneNumber });
    return { success: true };
  } catch (err) {
    return {
      success: false,
      message: extractErrorMessage(
        err,
        "Failed to send OTP. Please try again."
      ),
    };
  }
}

const extractToken = (data) =>
  data?.token ||
  data?.access_token ||
  data?.Token ||
  data?.authToken ||
  data?.response?.token ||
  data?.response?.Token ||
  data?.response?.authToken ||
  null;

const clearRkSession = () => {
  deleteCookie(AUTH_TOKEN_COOKIE);
  deleteCookie(OUTLET_ID_COOKIE);
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("rk_user_phone");
  }
};

export async function verifyOtpAndSignIn({ phoneNumber, otp }) {
  try {
    const resp = await signIn({ phoneNumber, otp });
    const data = resp?.data;
    let authToken = resp?.headers?.authorization || extractToken(data);
    if (authToken) {
      setCookie(AUTH_TOKEN_COOKIE, authToken);
    }
    window.localStorage.setItem("rk_user_phone", phoneNumber);

    try {
      const userResp = await signInUser();
      const outletId = userResp?.data?.response?.outlet?.id;
      if (outletId != null) {
        setCookie(OUTLET_ID_COOKIE, String(outletId));
      }
      const refreshedToken =
        userResp?.headers?.authorization || extractToken(userResp?.data);
      if (refreshedToken) {
        authToken = refreshedToken;
        setCookie(AUTH_TOKEN_COOKIE, refreshedToken);
      }
    } catch (userErr) {
      clearRkSession();
      return {
        success: false,
        message: extractErrorMessage(userErr, "Failed to fetch user details"),
      };
    }

    if (!authToken || !getCookie(AUTH_TOKEN_COOKIE)) {
      clearRkSession();
      return {
        success: false,
        message: "Failed to complete sign in. Please try again.",
      };
    }

    return { success: true, data };
  } catch (err) {
    clearRkSession();
    const status = err?.response?.status;
    if (status === 400) {
      return { success: false, message: "Invalid OTP" };
    }
    return {
      success: false,
      message: extractErrorMessage(
        err,
        "Something went wrong. Please try again."
      ),
    };
  }
}
